import { z } from 'zod';
import { RECURRENCE_CYCLES, RECURRING_STATUSES } from './recurring.js';
import { ColumnFiltersParamSchema } from './columnFilters.js';

const Cuid = z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.');

const DayString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'التاريخ يجب أن يكون بصيغة سنة-شهر-يوم.');

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const RecurringLineSchema = z.object({
  serviceId: Cuid.nullable().optional(),
  description: z.string().trim().min(1, 'وصف البند مطلوب.').max(500),
  quantity: z.number().min(0.001, 'الكمية أكبر من صفر.').max(999_999),
  unit: z.string().trim().min(1).max(40).default('خدمة'),
  unitPrice: z.number().min(0).max(99_999_999_999.99),
  discount: z.number().min(0).max(99_999_999_999.99).default(0),
});

export const CreateRecurringSchema = z
  .object({
    accountId: Cuid,
    contractId: Cuid.nullable().optional(),
    title: z.string().trim().min(2, 'عنوان الاشتراك مطلوب.').max(200),
    notes: optionalText(1000),
    terms: optionalText(2000),

    cycle: z.enum(RECURRENCE_CYCLES).default('MONTHLY'),
    startDate: DayString,
    endDate: DayString.nullable().optional(),
    dayOfMonth: z.number().int().min(1).max(31).default(1),

    discount: z.number().min(0).max(99_999_999_999.99).default(0),
    taxRate: z.number().min(0).max(100).optional(),
    dueDays: z.number().int().min(0).max(365).optional(),
    autoIssue: z.boolean().default(false),

    lines: z.array(RecurringLineSchema).min(1, 'أضف بندًا واحدًا على الأقل.').max(100),
  })
  .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    message: 'تاريخ الانتهاء قبل تاريخ البدء.',
    path: ['endDate'],
  });

export type CreateRecurringInput = z.infer<typeof CreateRecurringSchema>;

/**
 * التعديل لا يشمل تاريخ البدء.
 *
 * تغييره بعد أن صدرت فواتير يجعل الدورات المُصدَرة لا تطابق أي دورة يحسبها
 * النظام، فتظهر فواتير يتيمة لا يُعرف لأي شهر هي.
 */
export const UpdateRecurringSchema = z.object({
  title: z.string().trim().min(2).max(200).optional(),
  notes: optionalText(1000),
  terms: optionalText(2000),
  endDate: DayString.nullable().optional(),
  dayOfMonth: z.number().int().min(1).max(31).optional(),
  discount: z.number().min(0).max(99_999_999_999.99).optional(),
  taxRate: z.number().min(0).max(100).optional(),
  dueDays: z.number().int().min(0).max(365).optional(),
  autoIssue: z.boolean().optional(),
  lines: z.array(RecurringLineSchema).min(1).max(100).optional(),
});

export type UpdateRecurringInput = z.infer<typeof UpdateRecurringSchema>;

export const RecurringListQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.enum(RECURRING_STATUSES).optional(),
  accountId: Cuid.optional(),
  /** مدى «الإصدار التالي» — ما سيُصدَر بين اليومين، واليوم الأخير داخلٌ كلّه. */
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  /** فرزٌ من رأس العمود — بلا قيمةٍ يبقى الترتيب الافتراضي (الحالة ثم الإصدار التالي). */
  sort: z.string().trim().max(40).optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
  /** شروط الأعمدة من زرّ «فلتر» — انظر `columnFilters.ts`. */
  f: ColumnFiltersParamSchema,
});

export type RecurringListQuery = z.infer<typeof RecurringListQuerySchema>;

export const SetRecurringStatusSchema = z.object({
  status: z.enum(['ACTIVE', 'PAUSED', 'ENDED'], {
    errorMap: () => ({ message: 'حالة غير صالحة.' }),
  }),
});

export type SetRecurringStatusInput = z.infer<typeof SetRecurringStatusSchema>;

/* ==========================================================================
   اشتراكات الموردين الدورية
   ========================================================================== */

/** بند قالب فاتورة المورد: يُحمَّل على حساب في الدليل لا على خدمة من الكتالوج. */
export const RecurringBillLineSchema = z.object({
  accountId: Cuid,
  description: z.string().trim().min(1, 'وصف البند مطلوب.').max(500),
  quantity: z.number().min(0.001, 'الكمية أكبر من صفر.').max(999_999),
  unitPrice: z.number().min(0).max(99_999_999_999.99),
});

export const CreateRecurringBillSchema = z
  .object({
    supplierId: Cuid,
    title: z.string().trim().min(2, 'عنوان الاشتراك مطلوب.').max(200),
    notes: optionalText(1000),

    cycle: z.enum(RECURRENCE_CYCLES).default('MONTHLY'),
    startDate: DayString,
    endDate: DayString.nullable().optional(),
    /** عدد الفواتير الأقصى — بديلٌ عن تاريخ الانتهاء. */
    maxRuns: z.number().int().min(1).max(600).nullable().optional(),
    dayOfMonth: z.number().int().min(1).max(31).default(1),

    discount: z.number().min(0).max(99_999_999_999.99).default(0),
    taxRate: z.number().min(0).max(100).optional(),
    /** فارغة = مهلة المورد المتفق عليها وقت كل إصدار. */
    dueDays: z.number().int().min(0).max(365).nullable().optional(),
    /** الافتراضي مسودة للمراجعة — انظر autoReceive في schema.prisma. */
    autoReceive: z.boolean().default(false),

    lines: z.array(RecurringBillLineSchema).min(1, 'أضف بندًا واحدًا على الأقل.').max(100),
  })
  .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    message: 'تاريخ الانتهاء قبل تاريخ البدء.',
    path: ['endDate'],
  });

export type CreateRecurringBillInput = z.infer<typeof CreateRecurringBillSchema>;

/** التعديل لا يشمل المورد ولا الدورة ولا تاريخ البدء — للسبب نفسه في UpdateRecurringSchema. */
export const UpdateRecurringBillSchema = z.object({
  title: z.string().trim().min(2).max(200).optional(),
  notes: optionalText(1000),
  endDate: DayString.nullable().optional(),
  maxRuns: z.number().int().min(1).max(600).nullable().optional(),
  dayOfMonth: z.number().int().min(1).max(31).optional(),
  discount: z.number().min(0).max(99_999_999_999.99).optional(),
  taxRate: z.number().min(0).max(100).optional(),
  dueDays: z.number().int().min(0).max(365).nullable().optional(),
  autoReceive: z.boolean().optional(),
  lines: z.array(RecurringBillLineSchema).min(1).max(100).optional(),
});

export type UpdateRecurringBillInput = z.infer<typeof UpdateRecurringBillSchema>;

export const RecurringBillListQuerySchema = RecurringListQuerySchema.omit({
  accountId: true,
}).extend({
  supplierId: Cuid.optional(),
});

export type RecurringBillListQuery = z.infer<typeof RecurringBillListQuerySchema>;
